import React, { FC } from 'react';
import {
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Action, ActionSettings } from 'models/action';

type ActionRightSectionProps = {
    action: Action;
    handleSettingsChange: (settingKey: keyof ActionSettings, field: string, value: any) => void;
};

const decimalOptions = [0, 1, 2, 3, 4];

const ActionRightSection: FC<ActionRightSectionProps> = ({ action, handleSettingsChange }) => {
    const { t: translate } = useTranslation(['orders', 'manage-actions']);

    const renderSettingRow = (label: string, category: keyof typeof action.settings) => {
        const catSettings: any = action?.settings?.[category] ?? {};

        return (
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={4}>
                    <Typography variant="body2">{translate(`orders:${label}`)}:</Typography>
                </Grid>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={catSettings.include || false}
                                onChange={(e) =>
                                    handleSettingsChange(category as keyof ActionSettings, 'include', e.target.checked)
                                }
                                size="small"
                            />
                        }
                        label=""
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={catSettings.mandatory || false}
                                onChange={(e) =>
                                    handleSettingsChange(
                                        category as keyof ActionSettings,
                                        'mandatory',
                                        e.target.checked,
                                    )
                                }
                                size="small"
                            />
                        }
                        label=""
                    />
                </Grid>
            </Grid>
        );
    };

    function handleIsIntegerChange(checked: boolean): void {
        handleSettingsChange('numberOfUnits', 'isInteger', checked);
        if (checked) {
            handleSettingsChange('numberOfUnits', 'numberOfDecimals', undefined);
        }
    }

    return (
        <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {translate('manage-actions:SETTINGS')}
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {translate('manage-actions:INCLUDE')}
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {translate('orders:MANDATORY')}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                {renderSettingRow('COLOR_SHADE', 'colorOrShade')}
                {renderSettingRow('DESIGN', 'design')}
                {renderSettingRow('CONTACT_TYPE', 'contactType')}
                {renderSettingRow('OCCLUSION', 'occlusion')}
                {renderSettingRow('PONTIC_DESIGN', 'ponticDesign')}
                {renderSettingRow('RETAINMENT', 'retainment')}
                {renderSettingRow('IMPLANT_STEPS', 'implantSteps')}
                {renderSettingRow('DENTURE_STEPS', 'dentureSteps')}
                {renderSettingRow('TESTING_STEPS', 'testingSteps')}
                {renderSettingRow('CLASP_TEETH', 'claspTeeth')}
                {renderSettingRow('CLASP_MATERIAL', 'claspMaterial')}
                {renderSettingRow('ORTH_COLORS', 'orthColor')}
                {renderSettingRow('FACIAL_LOOP', 'facialLoop')}
                {renderSettingRow('CLASP_TYPES', 'claspType')}
                {renderSettingRow('SPRING_TYPES', 'springType')}
                {renderSettingRow('SPRING_TEETH', 'springTeeth')}
                {renderSettingRow('EXPANSION_SCREW', 'expansionScrew')}
                {renderSettingRow('PROTRUSION', 'protrusion')}

                {/* Number of Units */}
                <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <Grid item xs={4}>
                        <Typography variant="body2">{translate('orders:NO_OF_UNITS')}:</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={action.settings?.numberOfUnits?.include || false}
                                    onChange={(e) => handleSettingsChange('numberOfUnits', 'include', e.target.checked)}
                                    disabled={action.costPerTooth}
                                    size="small"
                                />
                            }
                            label=""
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={action.settings?.numberOfUnits?.mandatory || false}
                                    onChange={(e) =>
                                        handleSettingsChange('numberOfUnits', 'mandatory', e.target.checked)
                                    }
                                    disabled={action.costPerTooth}
                                    size="small"
                                />
                            }
                            label=""
                        />
                    </Grid>
                </Grid>

                {/* Integer checkbox */}
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">{translate('manage-actions:INTEGER')}:</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={action.settings?.numberOfUnits?.isInteger || false}
                                    onChange={(e) => handleIsIntegerChange(e.target.checked)}
                                    disabled={action.costPerTooth}
                                    size="small"
                                />
                            }
                            label=""
                        />
                    </Grid>
                </Grid>

                {/* Number of decimals */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                        <Typography variant="body2">{translate('manage-actions:NO_OF_DEICMALS')}:</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={action.settings?.numberOfUnits?.numberOfDecimals || ''}
                                onChange={(e) =>
                                    handleSettingsChange('numberOfUnits', 'numberOfDecimals', e.target.value)
                                }
                                disabled={
                                    (!action.settings?.numberOfUnits?.include &&
                                        !action.settings?.numberOfUnits?.mandatory) ||
                                    action.costPerTooth ||
                                    action.settings?.numberOfUnits?.isInteger
                                }
                            >
                                {decimalOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Header */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                        <Typography variant="body2">{translate('manage-actions:HEADER')}:</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            size="small"
                            label={translate('manage-actions:HEADER')}
                            value={action.settings?.numberOfUnits?.header || ''}
                            onChange={(e) => handleSettingsChange('numberOfUnits', 'header', e.target.value)}
                            disabled={
                                (!action.settings?.numberOfUnits?.include &&
                                    !action.settings?.numberOfUnits?.mandatory) ||
                                action.costPerTooth
                            }
                            required={
                                action.settings?.numberOfUnits?.include || action.settings?.numberOfUnits?.mandatory
                            }
                            sx={{
                                '& .MuiInputLabel-asterisk': {
                                    color: 'red',
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};
export default ActionRightSection;
